import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Map as MapIcon, MoreVertical } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item';
import { cn } from '@/lib/utils';
import { ORGANIZATION } from 'components/IAP/ICSConstants';
import { useOrgActions } from 'components/IAP/hooks/orgActionHooks';
import type { OrgEventAutomationRule } from 'hooks/orgEvents/orgEventAutomationHooks';
import { eventResponseActionProgress } from 'hooks/orgEvents/orgEventChecklistItems';
import {
  useDeleteOrgEvent,
  useUpdateOrgEvent,
  type OrgEvent,
} from 'hooks/orgEvents/orgEventHooks';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { openMapItemPopup } from '../LayersTab/utils/openMapItemPopup';
import { CfaHeleneImpactSections } from './CfaHeleneImpactSections';
import { OrgEventDetailsBody } from './OrgEventDetailsBody';
import { OrgEventPopupContent } from './OrgEventPopupContent';
import { severityBadgeClass } from './severityBadgeClass';

export { severityBadgeClass } from './severityBadgeClass';

function ResponseProgressRing({
  complete,
  total,
}: {
  complete: number;
  total: number;
}) {
  const size = 14;
  const percent = total === 0 ? 0 : Math.round((complete / total) * 100);
  const strokeWidth = 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (percent / 100) * circumference;
  const done = total > 0 && complete >= total;

  return (
    <span className="tw:inline-flex tw:shrink-0" aria-hidden="true">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="tw:-rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="tw:text-muted-foreground/40"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          className={done ? 'tw:text-emerald-500' : 'tw:text-primary'}
        />
      </svg>
    </span>
  );
}

export type OrgEventListItemProps = {
  event: OrgEvent;
  defaultOpen?: boolean;
  /** When false, skip scrolling the list to this item (preview dialog). */
  scrollOnOpen?: boolean;
  onTriggerRuleClick?: (rule: OrgEventAutomationRule) => void;
  onRelatedEventClick?: (event: OrgEvent) => void;
  /** Archive / restore controls. Off in preview cards. */
  showArchiveActions?: boolean;
  /** Edit control. Off in preview cards. */
  showEditAction?: boolean;
  onEdit?: (event: OrgEvent) => void;
};

/**
 * Canonical org-event card: badges, name, type · region, then Summary / Trigger
 * / timestamps and Assets at Risk + Courses of Action.
 */
export function OrgEventListItem({
  event,
  defaultOpen,
  scrollOnOpen = true,
  onTriggerRuleClick,
  onRelatedEventClick,
  showArchiveActions = false,
  showEditAction = false,
  onEdit,
}: OrgEventListItemProps) {
  const [open, setOpen] = useState(!!defaultOpen);
  const { mapView } = useUscgMapContext();
  const orgActions = useOrgActions(ORGANIZATION);
  const checklistProgress = useMemo(
    () =>
      eventResponseActionProgress({
        items: event.response_checklist_items,
        actionsLoaded: orgActions.isSuccess,
        actions: (orgActions.data ?? []).map((action) => ({
          actionId: action.action_id,
          status: action.status,
          archived: action.archived,
        })),
        server: event.response_checklist_progress,
      }),
    [
      event.response_checklist_items,
      event.response_checklist_progress,
      orgActions.data,
      orgActions.isSuccess,
    ]
  );

  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  useEffect(() => {
    if (!defaultOpen || !scrollOnOpen) return;
    itemRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [defaultOpen, scrollOnOpen]);

  const canFocus =
    typeof event.lon === 'number' &&
    typeof event.lat === 'number' &&
    Number.isFinite(event.lon) &&
    Number.isFinite(event.lat);

  return (
    <div ref={itemRef} className="tw:min-w-0 tw:max-w-full">
      <Item
        variant="outline"
        size="xs"
        className="tw:min-w-0 tw:max-w-full tw:flex-col tw:items-stretch tw:overflow-hidden tw:p-0 tw:bg-card"
      >
        <Collapsible
          open={open}
          onOpenChange={setOpen}
          className="tw:min-w-0 tw:w-full tw:overflow-hidden"
        >
          <div
            className="tw:flex tw:cursor-pointer tw:items-start tw:gap-2 tw:p-2.5 tw:transition-colors tw:hover:bg-muted/40"
            onClick={() => setOpen((prev) => !prev)}
          >
            <ItemContent className="tw:min-w-0 tw:flex-1 tw:gap-1">
              <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
                <Badge
                  className={cn(
                    'tw:shrink-0',
                    severityBadgeClass(event.severity)
                  )}
                >
                  {event.severity || 'Medium'}
                </Badge>
                <Badge variant="outline" className="tw:shrink-0">
                  {event.status || 'Active'}
                </Badge>
                {checklistProgress ? (
                  <Badge
                    variant={
                      checklistProgress.complete === checklistProgress.total
                        ? 'green'
                        : 'outline'
                    }
                    className="tw:shrink-0"
                  >
                    <ResponseProgressRing
                      complete={checklistProgress.complete}
                      total={checklistProgress.total}
                    />
                    {checklistProgress.complete} of {checklistProgress.total}{' '}
                    response actions complete
                  </Badge>
                ) : null}
              </div>
              <ItemTitle className="tw:text-sm tw:font-medium tw:leading-snug">
                {event.name}
              </ItemTitle>
              <ItemDescription className="tw:text-xs tw:text-muted-foreground">
                {[event.type, event.region].filter(Boolean).join(' · ') ||
                  'Org event'}
              </ItemDescription>
            </ItemContent>
            <ItemActions className="tw:shrink-0 tw:gap-1">
              {canFocus ? (
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Focus event on map"
                  onClick={(click) => {
                    click.stopPropagation();
                    if (!mapView || !canFocus) return;
                    void openMapItemPopup({
                      mapView,
                      longitude: event.lon as number,
                      latitude: event.lat as number,
                      title: event.name,
                      zoom: 7,
                      content: <OrgEventPopupContent event={event} />,
                    });
                  }}
                >
                  <MapIcon className="tw:h-4 tw:w-4" />
                </Button>
              ) : null}
              {showEditAction || showArchiveActions ? (
                <OrgEventOverflowMenu
                  event={event}
                  showEditAction={showEditAction}
                  showArchiveActions={showArchiveActions}
                  onEdit={onEdit}
                />
              ) : null}
              <CollapsibleTrigger
                render={
                  <Button
                    type="button"
                    size="icon-sm"
                    variant="ghost"
                    aria-label={open ? 'Collapse event' : 'Expand event'}
                    onClick={(click) => click.stopPropagation()}
                  />
                }
              >
                <ChevronDown
                  className={cn(
                    'tw:h-4 tw:w-4 tw:transition-transform',
                    open && 'tw:rotate-180'
                  )}
                />
              </CollapsibleTrigger>
            </ItemActions>
          </div>
          <CollapsibleContent className="tw:min-w-0 tw:overflow-hidden">
            <div className="tw:min-w-0 tw:space-y-3 tw:overflow-hidden tw:border-t tw:border-border tw:px-2.5 tw:py-2">
              <OrgEventDetailsBody
                event={event}
                onTriggerRuleClick={onTriggerRuleClick}
                onRelatedEventClick={onRelatedEventClick}
              />
              <CfaHeleneImpactSections
                responseChecklistActionIds={event.response_checklist_action_ids}
                responseChecklistItems={event.response_checklist_items}
                orgEventId={event.id}
                eventName={event.name}
                includeAssets
                impactedResourceIds={event.impacted_resource_ids}
                orgEventOverride={event}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </Item>
    </div>
  );
}

function OrgEventOverflowMenu({
  event,
  showEditAction,
  showArchiveActions,
  onEdit,
}: {
  event: OrgEvent;
  showEditAction: boolean;
  showArchiveActions: boolean;
  onEdit?: (event: OrgEvent) => void;
}) {
  const [archiveOpen, setArchiveOpen] = useState(false);
  const deleteEvent = useDeleteOrgEvent();
  const updateEvent = useUpdateOrgEvent();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              aria-label="Event options"
              onClick={(click) => click.stopPropagation()}
            />
          }
        >
          <MoreVertical className="tw:h-4 tw:w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="dark tw:w-40">
          {showEditAction ? (
            <DropdownMenuItem onClick={() => onEdit?.(event)}>
              Edit
            </DropdownMenuItem>
          ) : null}
          {showArchiveActions && event.archived ? (
            <DropdownMenuItem
              disabled={updateEvent.isPending}
              onClick={() => {
                void updateEvent.mutateAsync({
                  id: event.id,
                  archived: false,
                });
              }}
            >
              Restore
            </DropdownMenuItem>
          ) : null}
          {showArchiveActions && !event.archived ? (
            <DropdownMenuItem onClick={() => setArchiveOpen(true)}>
              Archive
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent className="dark">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive event</AlertDialogTitle>
            <AlertDialogDescription>
              Archive this event? It will move to Archived.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteEvent.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              type="button"
              variant="destructive"
              disabled={deleteEvent.isPending}
              onClick={() => {
                void deleteEvent.mutateAsync(event.id).then(() => {
                  setArchiveOpen(false);
                });
              }}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
